import { isPlainObject, truncate } from '@vestigas/utils';

import { COLUMN_SEPARATOR } from './constants';

export const SUPPORTED_CSV_DELIMITERS = [COLUMN_SEPARATOR, ';'] as const;

// Current papaparse ensures that the errors and meta properties are always present.
export type CsvParseResult = {
  data: Array<Record<string, unknown>>;
  errors: Array<{ code?: string; message?: string }>;
  meta: { delimiter?: string; fields?: string[] };
};

export function reverseColumnMapping(
  columnMapping?: Record<string, string>,
): Record<string, string> | undefined {
  if (!columnMapping) return undefined;
  return Object.fromEntries(
    Object.entries(columnMapping).map(([key, value]) => [value, key]),
  );
}

export function reverseColumnMappingForApi(
  columnMapping?: Record<string, string>,
): Record<string, string> | undefined {
  if (!columnMapping) return undefined;
  return Object.fromEntries(
    Object.entries(columnMapping).map(([key, value]) => [value.trim(), key]),
  );
}

export function isCsvParseResult(value: unknown): value is CsvParseResult {
  if (!value || !isPlainObject(value)) {
    return false;
  }

  return (
    Array.isArray(value.data) &&
    Array.isArray(value.errors) &&
    isPlainObject(value.meta)
  );
}

function hasNoParsedContent(parsedRows: CsvParseResult): boolean {
  return parsedRows.data.length === 0;
}

function hasUnsupportedDetectedDelimiter(
  parsedRows: CsvParseResult,
  supportedDelimiters: readonly string[],
): boolean {
  const detectedDelimiter = parsedRows.meta.delimiter;
  return (
    typeof detectedDelimiter === 'string' &&
    detectedDelimiter.length > 0 &&
    !supportedDelimiters.includes(detectedDelimiter)
  );
}

export function hasInconsistentFieldCountError(
  parsedRows: CsvParseResult,
): boolean {
  return Boolean(
    parsedRows.errors.some(
      (error) =>
        error.code === 'TooFewFields' || error.code === 'TooManyFields',
    ),
  );
}

export function isUnsupportedDelimiterError(
  parsedRows: CsvParseResult,
  supportedDelimiters: readonly string[] = SUPPORTED_CSV_DELIMITERS,
): boolean {
  if (hasNoParsedContent(parsedRows)) {
    return false;
  }

  return hasUnsupportedDetectedDelimiter(parsedRows, supportedDelimiters);
}

export function getReadableSupportedCsvDelimiters(
  supportedDelimiters: readonly string[] = SUPPORTED_CSV_DELIMITERS,
): string {
  return supportedDelimiters.map((delimiter) => `"${delimiter}"`).join(', ');
}

const FILE_NAME_MAX_LENGTH = 64;
const FILE_NAME_TAIL_PRESERVE = 16;

export function truncatedFileName(fileName: string) {
  const extensionStart = fileName.lastIndexOf('.');
  const extensionLength =
    extensionStart > 0 ? fileName.length - extensionStart : 0;

  return truncate(
    fileName,
    FILE_NAME_MAX_LENGTH,
    extensionLength + FILE_NAME_TAIL_PRESERVE,
  );
}
